import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { FileDown, ClipboardCheck, Car, FileCheck2, History, Wrench } from "lucide-react";
import { AED } from "../../lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OverviewPanel = ({ bookings, docs, onGoTab }) => {
  const list = bookings || [];
  const current = list.find((b) => b.display_status === "active") || list.find((b) => b.display_status === "upcoming");
  const history = list.filter((b) => b.display_status === "completed" || b.status === "cancelled");
  const verified = (docs || []).filter((d) => d.status === "verified").length;

  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="overview-panel">
      {/* Current agreement */}
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 md:col-span-2" data-testid="current-agreement-card">
        <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-3 flex items-center gap-2"><Car className="w-4 h-4 text-[#009900]" /> Current agreement</h3>
        {current ? (
          <div className="flex flex-wrap items-center gap-4">
            <img src={current.vehicle.image} alt={current.vehicle.name} className="w-24 h-16 object-cover rounded" />
            <div className="flex-1 min-w-[200px]">
              <div className="font-bold text-[#0F1E17]">{current.vehicle.name} <span className="font-mono text-xs text-[#009900]">{current.booking_ref}</span></div>
              <div className="text-xs text-[#57685e] mt-1">
                {dayjs(current.pickup_datetime).format("DD MMM HH:mm")} → {dayjs(current.return_datetime).format("DD MMM HH:mm")} · {AED(current.quote.total)}
                {current.checkin?.status === "completed" && ` · RA ${current.checkin.ra_number}`}
                {current.flight?.number && ` · Flight ${current.flight.number}`}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <a href={`${API_BASE}/bookings/${current.booking_ref}/invoice.pdf`} target="_blank" rel="noreferrer"
                className="border-2 border-[#144733] text-[#144733] rounded px-3 py-2 text-[11px] font-medium uppercase tracking-wide hover:bg-[#f0f5f0] flex items-center gap-1.5" data-testid="overview-invoice-btn">
                <FileDown className="w-3.5 h-3.5" /> Invoice
              </a>
              {current.checkin?.status === "completed" ? (
                <a href={`${API_BASE}/bookings/${current.booking_ref}/agreement.pdf`} target="_blank" rel="noreferrer"
                  className="border-2 border-[#009900] text-[#009900] rounded px-3 py-2 text-[11px] font-medium uppercase tracking-wide hover:bg-[#f0f9f0] flex items-center gap-1.5" data-testid="overview-agreement-btn">
                  <FileDown className="w-3.5 h-3.5" /> Agreement
                </a>
              ) : (
                <Link to={`/checkin/${current.booking_ref}`}
                  className="btn-cta rounded px-3 py-2 text-[11px] flex items-center gap-1.5" data-testid="overview-checkin-btn">
                  <ClipboardCheck className="w-3.5 h-3.5" /> Check in
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#57685e]">No active rental right now. <Link to="/" className="text-[#009900] underline">Book your next car</Link>.</div>
        )}
      </div>

      <button onClick={() => onGoTab("documents")} className="bg-white rounded-2xl border border-[#E2E8F0] p-5 text-left card-lift" data-testid="overview-docs-card">
        <FileCheck2 className="w-6 h-6 text-[#009900] mb-2" />
        <div className="font-bold text-sm text-[#0F1E17]">Vehicle documentation</div>
        <div className="text-xs text-[#57685e] mt-1">{verified} of 3 documents verified — {verified >= 2 ? "you're handover-ready." : "upload yours for a paper-free handover."}</div>
      </button>

      <button onClick={() => onGoTab("services")} className="bg-white rounded-2xl border border-[#E2E8F0] p-5 text-left card-lift" data-testid="overview-services-card">
        <Wrench className="w-6 h-6 text-[#009900] mb-2" />
        <div className="font-bold text-sm text-[#0F1E17]">Service requests</div>
        <div className="text-xs text-[#57685e] mt-1">Maintenance, vehicle swap, toll top-up or a child seat — we call back within one business day.</div>
      </button>

      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 md:col-span-2" data-testid="overview-history-card">
        <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-3 flex items-center gap-2"><History className="w-4 h-4 text-[#009900]" /> Rental history</h3>
        {history.length === 0 ? (
          <p className="text-xs text-[#57685e]">Your completed rentals will appear here.</p>
        ) : (
          <div className="space-y-2">
            {history.slice(0, 5).map((b) => (
              <div key={b.booking_ref} className="flex flex-wrap justify-between gap-2 text-xs border-b border-[#f1f3f1] pb-2">
                <span className="font-mono font-semibold text-[#144733]">{b.booking_ref}</span>
                <span className="text-[#0F1E17]">{b.vehicle.name}</span>
                <span className="text-[#57685e]">{dayjs(b.pickup_datetime).format("DD MMM YY")}</span>
                <span className={b.status === "cancelled" ? "text-red-500" : "text-[#009900]"}>{b.status === "cancelled" ? "Cancelled" : AED(b.quote.total)}</span>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => onGoTab("bookings")} className="mt-3 text-xs font-semibold uppercase tracking-wide text-[#009900] hover:underline">All bookings →</button>
      </div>
    </div>
  );
};
